/**
 * Where the print page's back button goes.
 *
 * The page is reached from a lot of different buttons — a hike map, the plan
 * screen, a recording, a waypoint group, a hike list, the explore map — so
 * this deliberately does not know about any of them. "Back to where I was" is
 * whatever linked here, which the browser already records.
 *
 * History first, because only `history.back()` restores the scroll position
 * and — on a bfcache hit — the live page with its map camera intact. A URL
 * navigation reloads the page at its default view, which is not where anybody
 * was. The remaining rungs are for the arrivals where history is not there to
 * be used:
 *
 *   1. somewhere on this site, and there is history        -> history.back()
 *   2. somewhere on this site, but no history (a new tab)  -> that URL
 *   3. no usable referrer, but the sheet has a source      -> the source
 *   4. nothing to go on                                    -> home
 *
 * Rung 2 is not hypothetical: RecordingMap opens this page with
 * `window.open(..., '_blank')`, where the referrer is set but the tab's
 * history is one entry long, so `history.back()` would silently do nothing
 * and the button would look broken.
 *
 * Pure function of explicit inputs so it can be tested without a document.
 */

/** The print page's own path — never a useful thing to go "back" to. */
export const PRINT_MAPS_PATH = '/print-maps';

/** Last resort when there is no referrer and no source to return to. */
export const FALLBACK_URL = '/';

export type PrintMapBackTarget =
  | { kind: 'history' }
  | { kind: 'url'; url: string };

export interface PrintMapBackInput {
  /** `document.referrer` — empty for a direct arrival. */
  referrer: string | null | undefined;
  /** `window.location.origin`. */
  origin: string;
  /** `window.history.length` — 1 in a tab this page was opened into. */
  historyLength: number;
  /** The hike/recording/route this sheet is for, when there is one. */
  sourceUrl?: string | null;
}

export function printMapBackTarget(
  { referrer, origin, historyLength, sourceUrl }: PrintMapBackInput,
): PrintMapBackTarget {
  const from = sameSiteReferrer(referrer, origin);

  if (from) return historyLength > 1 ? { kind: 'history' } : { kind: 'url', url: from };
  if (sourceUrl) return { kind: 'url', url: sourceUrl };

  return { kind: 'url', url: FALLBACK_URL };
}

/**
 * The referring URL when it is somewhere on this site other than the print
 * page itself, else null.
 *
 * Off-site referrers are excluded because going "back" should not leave
 * HiiKER — someone who arrived from a search result wants the hike, not the
 * search result. The print page excludes itself because several of those
 * entry points land here with different params, and back would then step
 * through print-map states instead of leaving.
 */
function sameSiteReferrer(referrer: string | null | undefined, origin: string): string | null {
  if (!referrer) return null;

  let url: URL;
  try {
    // Parsed strictly, with no base: `document.referrer` is always absolute
    // or empty, so anything relative is a caller error rather than a referrer
    // and must not be resolved against our own origin.
    url = new URL(referrer);
  } catch {
    return null;
  }

  if (url.origin !== origin) return null;
  if (isPrintMapsPath(url.pathname)) return null;

  return referrer;
}

function isPrintMapsPath(pathname: string): boolean {
  return pathname === PRINT_MAPS_PATH || pathname === `${PRINT_MAPS_PATH}/`;
}
